import { ComponentProps, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { MatchaAutoBanner } from '@/components/MatchaAutoBanner';
import { SankeyPreview } from '@/components/TokenProfile/SankeyPreview';
import { TransactionModule } from '@/components/TransactionModule';
import { TradeEngineProvider } from '@/components/TransactionModule/useTradeEngine';

import { useUrlTokenPair } from '@/hooks/transactionModule/useUrlTokenPair';
import { useCheckoutMode, CheckoutModes } from '@/hooks/useCheckoutMode';

import { Panel } from '@/ui/Panel';

import { classNames } from '@/utils/classnames';
import { routes } from '@/utils/routes';

type TradePageContentProps = ComponentProps<'div'> & {
  iFrameContext: boolean;
};

export const TradePageContent = (props: TradePageContentProps) => {
  const { iFrameContext, className, ...otherProps } = props;

  const [checkoutMode] = useCheckoutMode();

  const [autoBannerVisible, setAutoBannerVisible] = useState(false);

  const {
    sellToken,
    buyToken,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    handleFlipSelectedTokens,
    handleSelectToken,
    handleSelectPair,
  } = useUrlTokenPair(routes.TRADE);

  // Focus the sell amount on page load with a small delay to make sure the input is mounted
  useEffect(() => {
    const timer = setTimeout(() => document.getElementById('sellAmount')?.focus(), 800);
    return () => clearTimeout(timer);
  }, []);

  const showSankeyDiagram =
    !iFrameContext && checkoutMode === 'market' && sellTokenChainId === buyTokenChainId;

  return (
    <div
      className={twMerge(
        classNames(
          'bg-transparent flex grow flex-col items-center px-0 tablet:px-3 h-[100dvh] pt-[62px]',
          'tablet:h-auto tablet:pt-[102px] [body[data-show-banner="true"]_&]:tablet:pt-[142px]',
          '[body[data-show-banner="true"]_&]:pt-[102px]',
          { 'bg-transparent h-[100dvh] tablet:h-auto': iFrameContext },
          { 'bg-special-background-app h-auto': !iFrameContext },
          className,
        ),
      )}
      {...otherProps}
    >
      <div
        className={classNames(
          'size-full flex flex-col items-center gap-3',
          'desktop:flex-row desktop:items-stretch desktop:justify-center',
          {
            'tablet:max-w-[434px]': iFrameContext,
          },
        )}
      >
        <TradeEngineProvider
          sellTokenChainId={sellTokenChainId}
          sellTokenAddress={sellTokenAddress}
          buyTokenChainId={buyTokenChainId}
          buyTokenAddress={buyTokenAddress}
        >
          <div
            className={classNames('size-full flex flex-col gap-3', {
              'max-w-[434px]': !iFrameContext,
            })}
          >
            <Panel className={iFrameContext ? 'h-full rounded-none tablet:rounded-2xl' : undefined}>
              <TransactionModule
                autoBannerVisible={autoBannerVisible}
                onFlipSelectedTokens={handleFlipSelectedTokens}
                onSelectToken={handleSelectToken}
                onSelectPair={handleSelectPair}
                setAutoBannerVisible={setAutoBannerVisible}
                sellTokenAddress={sellTokenAddress}
                sellTokenChainId={sellTokenChainId}
                buyTokenAddress={buyTokenAddress}
                buyTokenChainId={buyTokenChainId}
                variant={iFrameContext ? 'fullpage' : 'widget'}
                className={iFrameContext ? 'min-h-auto!' : undefined}
              />
            </Panel>
            {showSankeyDiagram && (
              <SankeyPreview
                buyToken={buyToken}
                sellToken={sellToken}
                chainId={buyTokenChainId ?? 1}
              />
            )}
            {autoBannerVisible &&
              (sellTokenChainId || buyTokenChainId) &&
              checkoutMode === CheckoutModes.market && (
                <MatchaAutoBanner
                  chainId={sellTokenChainId! || buyTokenChainId!}
                  setAutoBannerVisible={setAutoBannerVisible}
                />
              )}
          </div>
        </TradeEngineProvider>
      </div>
    </div>
  );
};
